import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { fmtErr } from "../lib/api";
import { BookOpen } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", captcha_answer: "", website: "" });
  const [captcha, setCaptcha] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const loadCaptcha = () => api.get("/auth/captcha").then((r) => setCaptcha(r.data)).catch(() => {});
  useEffect(() => { loadCaptcha(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ ...form, captcha_id: captcha?.captcha_id });
      navigate("/dashboard");
    } catch (err) {
      setError(fmtErr(err));
      loadCaptcha();
      setForm((f) => ({ ...f, captcha_answer: "" }));
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 focus:border-neutral-900 outline-none";

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 font-bold text-xl font-heading text-neutral-900 mb-8">
          <BookOpen size={24} /> GloriPub
        </Link>
        <div className="bg-white border border-neutral-200 rounded-lg p-8 shadow-sm">
          <h1 className="text-2xl font-semibold font-heading text-neutral-900">Create your author account</h1>
          <p className="text-sm text-neutral-500 mt-1 mb-6">Start your publishing journey</p>
          {error && <div data-testid="register-error" className="text-red-700 bg-red-50 border border-red-200 rounded-md p-3 text-sm mb-4">{error}</div>}
          <form onSubmit={submit} className="space-y-4">
            <input type="text" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <div>
              <label className="text-sm font-medium text-neutral-700">Full name</label>
              <input data-testid="register-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700">Email</label>
              <input data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700">Password (min 8 characters)</label>
              <input data-testid="register-password-input" type="password" required minLength={8} value={form.password} onChange={set("password")} className={inputCls} />
            </div>
            <div className="bg-neutral-50 border border-neutral-200 rounded-md p-4">
              <label className="text-sm font-medium text-neutral-700">Quick check: {captcha?.question || "Loading..."}</label>
              <input data-testid="register-captcha-input" required value={form.captcha_answer} onChange={set("captcha_answer")} className={inputCls} placeholder="Your answer" />
            </div>
            <button data-testid="register-submit-button" disabled={loading || !captcha}
              className="w-full py-2.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 font-medium text-sm transition-colors disabled:opacity-50">
              {loading ? "Creating account..." : "Create account"}
            </button>
          </form>
          <p className="text-sm text-neutral-500 mt-5 text-center">
            Already have an account? <Link to="/login" data-testid="go-login-link" className="text-neutral-900 font-medium hover:underline">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
